import type { RealtimeEvent } from './helpers';

import { differenceInSeconds, getSeconds, parseISO } from 'date-fns';
import * as React from 'react';
import { VictoryAxis, VictoryBar, VictoryChart } from 'victory';

import { Box } from '../../../components';
import { Dimen } from '../../../constants';
import { useInterval } from '../../../hooks';
import useDimens from '../useDimens';
import { chartTheme } from './helpers';

type Props = Readonly<{
  allEvents: RealtimeEvent[];
}>;

export default function BoxChartSeconds({ allEvents }: Props) {
  const { measuredEl, width } = useDimens();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [invalidateToken, setInvalidateToken] = React.useState<number>(
    Math.round(Math.random() * 1e5)
  );
  useInterval(() => setInvalidateToken(Math.round(Math.random() * 1e5)), 1000);

  const currentSecond = getSeconds(new Date());
  const dataValues = Array(60).fill(0);
  const relevantEvents = allEvents.filter(
    (event) => differenceInSeconds(new Date(), parseISO(event.timestamp)) < 60
  );
  for (const event of relevantEvents) {
    let diff = currentSecond - getSeconds(parseISO(event.timestamp));
    if (diff < 0) diff += 60;

    const index = 59 - diff;
    if (index < 0) continue;
    dataValues[index] += 1;
  }

  const max = Math.max(...dataValues);
  const mappedData = dataValues.map((v, i) => ({ x: i, y: v }));

  return (
    <Box title="Events Per Second">
      <div ref={measuredEl}>
        <VictoryChart
          width={width}
          padding={Dimen.legacySpacing}
          height={200}
          domain={{ y: [0, Math.max(4, max)] }}
          domainPadding={{ x: 10, y: [40, 0] }}
          theme={chartTheme}
        >
          <VictoryAxis tickFormat={(x) => `-${60 - x} sec`} tickCount={4} />
          <VictoryAxis
            dependentAxis
            style={{ grid: { stroke: '#eee' } }}
            tickFormat={(y) => Math.round(parseFloat(y))}
          />
          <VictoryBar data={mappedData} cornerRadius={2} barWidth={3} />
        </VictoryChart>
      </div>
    </Box>
  );
}
